import classNames from 'classnames'
import ResizeObserver from 'rc-resize-observer'
import type { FC } from 'react'
import React, { useCallback, useMemo, useRef, useState } from 'react'

import type { TextContainerProps } from './types'
import measure, { removeGhostContainer } from './util'
import { useUnmount } from '../../hooks'
import { isBrowser } from '../../utils/dom'
import makeId from '../../utils/make-id'
import rafWrapper from '../../utils/raf-wrapper'
import { mergeRefs } from '../../utils/react-helpers'
import Article from '../_primitives/article'
import type { EllipsisConfig } from '../_primitives/text'
import TextFormatter, { ELLIPSIS_STR, Expander } from '../text-formatter'

function getEllipsis(ellipsis?: boolean | EllipsisConfig): EllipsisConfig {
  if (!ellipsis) return {}

  return {
    rows: 1,
    expandable: false,
    ...(typeof ellipsis === 'object' ? ellipsis : null)
  }
}

type EllipsisState = {
  ellipsisText: string
  ellipsisContent: React.ReactNode | null
  isEllipsis: boolean
}

const initialState = {
  ellipsisText: '',
  ellipsisContent: null,
  isEllipsis: false
}

const TextContainer: FC<TextContainerProps> = React.forwardRef<
  HTMLDivElement,
  TextContainerProps
>(function TextContainer(props, ref) {
  const {
    id,
    as: asProps = 'p',
    className,
    columnGap,
    columns,
    children,
    ellipsis,
    ...rest
  } = props
  const identifierRef = useRef<string>(id || makeId())
  const contentRef = useRef<HTMLDivElement | null>(null)

  const [rafId, setRafId] = useState<number>()
  const ellipsisOptions = useMemo(() => getEllipsis(ellipsis), [ellipsis])
  const [ellipsisState, setEllipsisState] =
    useState<EllipsisState>(initialState)
  const [expanded, setExpanded] = useState(false)
  const { ellipsisText, ellipsisContent, isEllipsis } = ellipsisState
  const { rows, suffix, onExpand, expandable } = ellipsisOptions

  const handleOnExpandClick = useCallback(() => {
    if (expandable && !onExpand) {
      setExpanded(!expanded)
    }
    if (onExpand) {
      onExpand()
    }
  }, [expandable, expanded, onExpand])

  const renderExpand = useCallback(() => {
    if (!expandable) return null

    return (
      <Expander
        key={`text-container-expander-${identifierRef.current}`}
        className="csu-tui-text-container-expander"
        position="absolute"
        right={expanded ? '0' : '0.75rem'}
        bottom={expanded ? '-0.6rem' : '0.1rem'}
        aria-label={expanded ? 'click for less text' : 'click for more text'}
        onClick={handleOnExpandClick}
      >
        {expanded ? 'less' : 'more'}
      </Expander>
    )
  }, [expandable, expanded, handleOnExpandClick])

  const renderOperations = useCallback(() => {
    return [renderExpand()].filter(node => node)
  }, [renderExpand])

  const syncEllipsis = useCallback(() => {
    // @ts-ignore
    if (!rows || rows < 0 || !contentRef.current || expanded) return

    // Do not measure if not browser render
    if (!isBrowser) return

    const { content, text, ellipsis } = measure(
      identifierRef.current,
      // @ts-ignore
      contentRef.current,
      { rows, suffix },
      children,
      renderOperations(),
      ELLIPSIS_STR
    )
    if (ellipsisText !== text || isEllipsis !== ellipsis) {
      setEllipsisState({
        ellipsisText: text,
        ellipsisContent: content,
        isEllipsis: ellipsis
      })
    }
  }, [
    children,
    contentRef,
    ellipsisText,
    expanded,
    isEllipsis,
    renderOperations,
    rows,
    suffix
  ])

  const handleResize = useCallback(() => {
    rafWrapper.cancel(rafId)
    setRafId(rafWrapper(syncEllipsis))
  }, [rafId, syncEllipsis])

  useUnmount(() => {
    removeGhostContainer(identifierRef.current)
  })

  return (
    <ResizeObserver onResize={handleResize}>
      <Article
        className={classNames(
          {
            'csg-tui-article': true
          },
          className
        )}
        ref={mergeRefs(ref, contentRef)}
        position={expanded ? 'relative' : undefined}
        {...{ columnGap, columns, ...rest }}
      >
        <TextFormatter as={asProps} id={identifierRef.current}>
          {`${isEllipsis && !expanded ? ellipsisContent : children}`}
          {`${isEllipsis && !expanded ? ELLIPSIS_STR : ''}`}
          {`${suffix ? suffix : ''}`}
          {isEllipsis && !expanded && renderOperations()}
        </TextFormatter>
        {isEllipsis && expanded && renderOperations()}
      </Article>
    </ResizeObserver>
  )
})

export default TextContainer
